import { useState, useEffect, lazy, Suspense } from 'react'
import Layout from './Layout'
import StagingBanner from './components/StagingBanner'
import LogoManrina from './components/LogoManrina'
import UpdateBanner from './components/UpdateBanner'
import { auth, setStoredUser, removeToken } from './api'

// === LAZY LOADING MODULES ===
const Dashboard = lazy(() => import('./Dashboard'))
const Caisse = lazy(() => import('./Caisse'))
const Evenement = lazy(() => import('./pages/Evenement/Evenement'))
const Produits = lazy(() => import('./Produits'))
const Utilisateurs = lazy(() => import('./Utilisateurs'))
const Tournees = lazy(() => import('./pages/Tournees/Tournees'))
const Admin = lazy(() => import('./Admin'))
const Facturation = lazy(() => import('./Facturation'))
const Notifications = lazy(() => import('./Notifications'))
const Transformation = lazy(() => import('./Transformation'))
const Lavwa = lazy(() => import('./pages/Lavwa/Lavwa'))
const Tanbou = lazy(() => import('./pages/Tanbou/Tanbou'))
const Fonds = lazy(() => import('./pages/Fonds/Fonds'))

// === LOADING SPINNER ===
function ModuleLoader() {
  return (
    <div className="flex items-center justify-center py-32">
      <div className="text-center">
        <div className="w-10 h-10 border-3 border-manrina-200 border-t-manrina-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-gray-400">Chargement...</p>
      </div>
    </div>
  )
}

// === AUTH HOOK ===
const useAuth = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [setupComplete, setSetupComplete] = useState(true)
  const [showSetup, setShowSetup] = useState(false)
  const [setupKey, setSetupKey] = useState('')
  const [authLoading, setAuthLoading] = useState(true)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    setAuthLoading(true)

    try {
      const setupData = await auth.checkSetup()
      setSetupComplete(setupData?.setupDone !== false)

      if (!setupData?.setupDone) {
        const urlParams = new URLSearchParams(window.location.search)
        const key = urlParams.get('key')
        if (key) {
          setSetupKey(key)
          setShowSetup(true)
        }
      }
    } catch (err) {
      console.error('Erreur check setup:', err)
      setSetupComplete(true)
    }

    // Verifier la session via le cookie httpOnly (source de verite)
    try {
      const freshUser = await auth.me()
      // Seuls les ADMIN et MEMBRE ont acces au BO
      if (freshUser && (freshUser.role === 'ADMIN' || freshUser.role === 'MEMBRE')) {
        setCurrentUser(freshUser)
        setIsLoggedIn(true)
        setStoredUser(freshUser)
      } else {
        removeToken()
      }
    } catch {
      // Cookie absent ou expire — pas de session
      removeToken()
    }

    setAuthLoading(false)
  }

  const handleLogin = async (email, password) => {
    try {
      const result = await auth.login(email, password)

      if (result?.user) {
        // Seuls les ADMIN et MEMBRE ont acces au BO
        if (result.user.role !== 'ADMIN' && result.user.role !== 'MEMBRE') {
          return { success: false, error: 'Acces reserve aux administrateurs et membres' }
        }
        setCurrentUser(result.user)
        setIsLoggedIn(true)
        return { success: true }
      }

      return { success: false, error: 'Erreur de connexion' }
    } catch (err) {
      return { success: false, error: err.message || 'Erreur de connexion' }
    }
  }

  const handleSetup = async (nom, email, password) => {
    try {
      const result = await auth.setup({ nom, email, password, setupKey })

      if (result?.success) {
        setSetupComplete(true)
        setShowSetup(false)
        window.history.replaceState({}, document.title, window.location.pathname)
        return { success: true }
      }

      return { success: false, error: result?.error || 'Erreur lors du setup' }
    } catch (err) {
      return { success: false, error: err.message || 'Erreur lors du setup' }
    }
  }

  const handleLogout = async () => {
    // Appel backend pour supprimer le cookie httpOnly
    try {
      await fetch('https://core.manrina.fr/auth/logout', {
        method: 'POST',
        credentials: 'include',
      })
    } catch { /* ignore */ }
    removeToken()
    setCurrentUser(null)
    setIsLoggedIn(false)
  }

  return {
    isLoggedIn,
    currentUser,
    setupComplete,
    showSetup,
    setupKey,
    authLoading,
    handleLogin,
    handleSetup,
    handleLogout
  }
}

// === SETUP PAGE ===
function SetupPage({ onSetup }) {
  const [form, setForm] = useState({ nom: '', email: '', password: '', confirmPassword: '' })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (form.password !== form.confirmPassword) {
      setError('Les mots de passe ne correspondent pas')
      return
    }

    if (form.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caracteres')
      return
    }

    setSaving(true)
    const result = await onSetup(form.nom, form.email, form.password)

    if (!result.success) {
      setError(result.error)
    } else {
      alert('Compte administrateur cree ! Vous pouvez maintenant vous connecter.')
    }

    setSaving(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-nature-800 via-nature-700 to-nature-600 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-6 shadow-xl animate-fade-up">
        <div className="text-center mb-6">
          <div className="mb-2 flex justify-center">
            <LogoManrina className="w-12 h-12" emojiClassName="text-4xl" />
          </div>
          <h1 className="text-xl font-display font-bold text-gray-800">Configuration initiale</h1>
          <p className="text-sm text-gray-500">Creez votre compte administrateur</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-gray-500 mb-1 block">Nom</label>
            <input
              type="text"
              value={form.nom}
              onChange={(e) => setForm({...form, nom: e.target.value})}
              className="w-full p-3 bg-gray-100 rounded-xl focus:ring-2 focus:ring-manrina-500 outline-none transition"
              required
            />
          </div>

          <div>
            <label className="text-sm text-gray-500 mb-1 block">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({...form, email: e.target.value})}
              className="w-full p-3 bg-gray-100 rounded-xl focus:ring-2 focus:ring-manrina-500 outline-none transition"
              required
            />
          </div>

          <div>
            <label className="text-sm text-gray-500 mb-1 block">Mot de passe</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({...form, password: e.target.value})}
              className="w-full p-3 bg-gray-100 rounded-xl focus:ring-2 focus:ring-manrina-500 outline-none transition"
              required
              minLength={8}
            />
          </div>

          <div>
            <label className="text-sm text-gray-500 mb-1 block">Confirmer mot de passe</label>
            <input
              type="password"
              value={form.confirmPassword}
              onChange={(e) => setForm({...form, confirmPassword: e.target.value})}
              className="w-full p-3 bg-gray-100 rounded-xl focus:ring-2 focus:ring-manrina-500 outline-none transition"
              required
            />
          </div>

          {error && <p className="text-red-500 text-sm text-center">{error}</p>}

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 bg-gradient-to-r from-nature-800 to-nature-600 text-white rounded-xl font-bold hover:opacity-90 transition"
          >
            {saving ? 'Creation...' : 'Creer le compte admin'}
          </button>
        </form>
      </div>
    </div>
  )
}

// === LOGIN PAGE ===
function LoginPage({ onLogin }) {
  const [form, setForm] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    const result = await onLogin(form.email, form.password)

    if (!result.success) {
      setError(result.error)
    }

    setSaving(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-nature-800 via-nature-700 to-nature-600 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-6 shadow-xl animate-fade-up">
        <div className="text-center mb-6">
          <div className="mb-2 flex justify-center">
            <LogoManrina className="w-12 h-12" emojiClassName="text-4xl" />
          </div>
          <h1 className="text-xl font-display font-bold text-gray-800">Manrina</h1>
          <p className="text-sm text-gray-500">Connectez-vous pour continuer</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-gray-500 mb-1 block">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({...form, email: e.target.value})}
              className="w-full p-3 bg-gray-100 rounded-xl focus:ring-2 focus:ring-manrina-500 outline-none transition"
              required
            />
          </div>

          <div>
            <label className="text-sm text-gray-500 mb-1 block">Mot de passe</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({...form, password: e.target.value})}
              className="w-full p-3 bg-gray-100 rounded-xl focus:ring-2 focus:ring-manrina-500 outline-none transition"
              required
            />
          </div>

          {error && <p className="text-red-500 text-sm text-center">{error}</p>}

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 bg-gradient-to-r from-manrina-500 to-manrina-600 text-white rounded-xl font-bold hover:opacity-90 transition"
          >
            {saving ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  )
}

// === APP PRINCIPALE ===
function App() {
  const authState = useAuth()
  const [activeModule, setActiveModule] = useState('dashboard')

  // Loading
  if (authState.authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-nature-800 via-nature-700 to-nature-600 flex items-center justify-center">
        <div className="text-center text-white animate-fade-up">
          <div className="mb-4 flex justify-center">
            <LogoManrina className="w-14 h-14" emojiClassName="text-5xl" />
          </div>
          <div className="w-8 h-8 border-3 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-white/60">Chargement...</p>
        </div>
      </div>
    )
  }

  // Setup
  if (!authState.setupComplete && authState.showSetup) {
    return <SetupPage onSetup={authState.handleSetup} />
  }

  // Login
  if (!authState.isLoggedIn) {
    return <LoginPage onLogin={authState.handleLogin} />
  }

  // Render with layout
  const moduleProps = {
    currentUser: authState.currentUser,
    onLogout: authState.handleLogout,
    onBack: () => setActiveModule('dashboard'),
  }

  const renderModule = () => {
    switch (activeModule) {
      case 'caisse':
        return <Caisse {...moduleProps} />
      case 'marche':
        return <Evenement {...moduleProps} />
      case 'tournees':
        return <Tournees {...moduleProps} />
      case 'transformation':
        return <Transformation {...moduleProps} />
      case 'lavwa':
        return <Lavwa {...moduleProps} />
      case 'tanbou':
        return <Tanbou {...moduleProps} />
      case 'fonds':
        return <Fonds {...moduleProps} />
      case 'facturation':
        return <Facturation {...moduleProps} />
      case 'produits':
        return <Produits {...moduleProps} />
      case 'utilisateurs':
        return <Utilisateurs {...moduleProps} />
      case 'notifications':
        return <Notifications {...moduleProps} />
      case 'admin':
        return <Admin {...moduleProps} />
      case 'dashboard':
      default:
        return <Dashboard {...moduleProps} onNavigate={setActiveModule} />
    }
  }

  return (
    <>
      <StagingBanner />
      <UpdateBanner />
      <Layout
        activeModule={activeModule}
        onSelect={setActiveModule}
        currentUser={authState.currentUser}
        onLogout={authState.handleLogout}
      >
        <Suspense fallback={<ModuleLoader />}>
          {renderModule()}
        </Suspense>
      </Layout>
    </>
  )
}

export default App
